import axios from "axios";
import api from "../services/api";
import { API_BASE } from "../config";
import { getCookie } from "../lib/cookie";

const refreshClient = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

refreshClient.interceptors.request.use((config) => {
  const method = (config.method || "get").toUpperCase();
  if (!/^(GET|HEAD|OPTIONS|TRACE)$/.test(method)) {
    const xsrf = getCookie("XSRF-TOKEN");
    if (xsrf) {
      config.headers = config.headers || {};
      config.headers["X-XSRF-TOKEN"] = xsrf;
    }
  }
  return config;
});

export const NO_REFRESH_PATHS = ["/login", "/register", "/verify", "/forgot-password", "/reset-password"];

export function shouldSkipRefresh(pathname = (typeof window !== "undefined" ? window.location.pathname : "/")) {
  return NO_REFRESH_PATHS.some((p) => pathname.startsWith(p));
}

function b64urlDecode(str) {
  str = str.replace(/-/g, "+").replace(/_/g, "/");
  while (str.length % 4) str += "=";
  return atob(str);
}

export async function refreshToken() {
  // Axios will attach X-XSRF-TOKEN automatically
  const { data } = await refreshClient.post("/api/auth/refresh", null);
  const newToken = data?.accessToken ?? data?.token;
  if (!newToken) throw new Error("No access token in refresh response");
  localStorage.setItem("accessToken", newToken);
  scheduleRefresh(newToken);
  return newToken;
}

export function scheduleRefresh(token) {
  if (shouldSkipRefresh()) return;

  clearTimeout(window.__rt);
  try {
    const parts = token.split(".");
    if (parts.length !== 3) throw new Error("bad jwt");
    const payload = JSON.parse(b64urlDecode(parts[1]));
    const msUntilExpiry = payload.exp * 1000 - Date.now();
    const refreshIn = Math.max(msUntilExpiry - 60_000, 5_000);
    window.__rt = setTimeout(() => {
      refreshToken().catch(() => {
        clearTimeout(window.__rt);
        localStorage.removeItem("accessToken");
        window.location.href = "/login";
      });
    }, refreshIn);
  } catch {
    window.__rt = setTimeout(() => {
      refreshToken().catch(() => {
        clearTimeout(window.__rt);
        localStorage.removeItem("accessToken");
        window.location.href = "/login";
      });
    }, 10 * 60_000);
  }
}

export async function primeCsrf() {
  try {
    // Use the same axios instance so cookies/withCredentials are consistent
    await api.get("/api/auth/csrf");
  } catch {
    // harmless if it fails; the next POST will retry once in the interceptor
  }
}

export function clearRefreshTimer() {
  clearTimeout(window.__rt);
}
